import React from "react";
import { useQuery } from "wasp/client/operations";
import { getServiceById, deleteService } from "wasp/client/operations";
import { useAuth } from "wasp/client/auth";
import { useNavigate, useParams, Link } from "react-router-dom";
import { DashboardLayout } from "../../client/components/Sidebar";
import { useLocationContext } from "../../client/contexts/LocationContext";
import {
  IndividualViewPageLayout,
  SidebarSection,
  ActionButtons,
  DetailField,
  ActionButtonLink,
  ActionButtonDanger,
} from "../../components/layout";
import {
  DollarSign,
  Tag,
  Phone,
  Edit,
  AlertCircle,
  Clock,
  Star,
  Award,
  CheckCircle,
  Trash2,
} from "lucide-react";
import { useAction } from "wasp/client/operations";
import { SubscribeButton } from "../content/components/SubscribeButton";

export default function ViewServicePage() {
  const { data: user } = useAuth();
  const { selectedLocation } = useLocationContext();
  const navigate = useNavigate();
  const { itemId } = useParams<{ itemId: string }>();
  const deleteServiceFn = useAction(deleteService);
  const [isDeleting, setIsDeleting] = React.useState(false);

  // Fetch the service data
  const {
    data: service,
    isLoading,
    error,
  } = useQuery(getServiceById, { itemId: itemId! });

  const handleGoBack = () => {
    if (selectedLocation?.locationCode) {
      navigate(`/locations/${selectedLocation.locationCode}/services`);
    } else {
      navigate("/services");
    }
  };

  const handleDelete = async () => {
    if (
      !window.confirm(
        "Are you sure you want to delete this service? This action cannot be undone.",
      )
    )
      return;
    setIsDeleting(true);
    try {
      await deleteServiceFn({ itemId: itemId! });
      if (selectedLocation?.locationCode) {
        navigate(`/locations/${selectedLocation.locationCode}/services`);
      } else {
        navigate("/services");
      }
    } catch (err) {
      alert("Failed to delete service. Please try again.");
    } finally {
      setIsDeleting(false);
    }
  };

  if (!user) {
    return (
      <DashboardLayout>
        <div className="p-6">
          <div className="text-center py-12">
            <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              Access Denied
            </h3>
            <p className="text-gray-600">
              You must be logged in to view services.
            </p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  if (isLoading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading service...</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  if (error || !service) {
    return (
      <DashboardLayout>
        <div className="p-6">
          <div className="text-center py-12">
            <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-content-text mb-2">
              Service Not Found
            </h3>
            <p className="text-content-text-muted">
              The service you're looking for doesn't exist.
            </p>
            <button
              onClick={handleGoBack}
              className="mt-4 bg-primary text-primary-foreground px-4 py-2 rounded-lg hover:bg-primary/90 transition-colors"
            >
              Go Back
            </button>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  const getCategoryColor = (category: string) => {
    const colors = {
      TUTORING: "bg-blue-100 text-blue-800",
      CLEANING: "bg-green-100 text-green-800",
      REPAIR: "bg-orange-100 text-orange-800",
      PERSONAL_CARE: "bg-pink-100 text-pink-800",
      TRANSPORTATION: "bg-purple-100 text-purple-800",
      EVENT_PLANNING: "bg-yellow-100 text-yellow-800",
      PHOTOGRAPHY: "bg-red-100 text-red-800",
      CONSULTING: "bg-indigo-100 text-indigo-800",
      OTHER: "bg-gray-100 text-gray-800",
    };
    return (
      colors[category as keyof typeof colors] || "bg-gray-100 text-gray-800"
    );
  };

  const getPriceTypeColor = (priceType: string) => {
    const colors = {
      HOURLY: "bg-blue-100 text-blue-800",
      FIXED: "bg-green-100 text-green-800",
      PER_SESSION: "bg-purple-100 text-purple-800",
      FREE: "bg-gray-100 text-gray-800",
    };
    return (
      colors[priceType as keyof typeof colors] || "bg-gray-100 text-gray-800"
    );
  };

  const getAvailabilityColor = (availability: string) => {
    const colors = {
      AVAILABLE: "bg-green-100 text-green-800",
      LIMITED: "bg-yellow-100 text-yellow-800",
      BUSY: "bg-red-100 text-red-800",
    };
    return (
      colors[availability as keyof typeof colors] || "bg-gray-100 text-gray-800"
    );
  };

  const currentPageUrl =
    typeof window !== "undefined" ? window.location.href : "";

  const breadcrumbItems = [
    {
      label: "Community",
      dropdownItems: [
        {
          label: "Buy and Sell",
          href: selectedLocation?.locationCode
            ? `/locations/${selectedLocation.locationCode}/buy-sell`
            : "/buy-sell",
        },
        {
          label: "Food Court",
          href: selectedLocation?.locationCode
            ? `/locations/${selectedLocation.locationCode}/food-court`
            : "/food-court",
        },
        {
          label: "Lost and Found",
          href: selectedLocation?.locationCode
            ? `/locations/${selectedLocation.locationCode}/lost-found`
            : "/lost-found",
        },
        {
          label: "Retail",
          href: selectedLocation?.locationCode
            ? `/locations/${selectedLocation.locationCode}/retail`
            : "/retail",
        },
        {
          label: "Services",
          href: selectedLocation?.locationCode
            ? `/locations/${selectedLocation.locationCode}/services`
            : "/services",
        },
      ],
    },
    {
      label: "Services",
      isClickable: true,
      href: selectedLocation?.locationCode
        ? `/locations/${selectedLocation.locationCode}/services`
        : "/services",
    },
  ];

  const isAdmin = user?.isAdmin;

  const sidebarContent = (
    <>
      {isAdmin && (
        <SidebarSection isFirst>
          <ActionButtons>
            <ActionButtonLink
              to={
                selectedLocation?.locationCode
                  ? `/locations/${selectedLocation.locationCode}/services/${service.id}/edit`
                  : `/services/${service.id}/edit`
              }
              icon={Edit}
            >
              Edit Service
            </ActionButtonLink>
            <ActionButtonDanger
              onClick={handleDelete}
              disabled={isDeleting}
              icon={Trash2}
            >
              {isDeleting ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white inline-block mr-2"></div>
                  Deleting...
                </>
              ) : (
                "Delete Service"
              )}
            </ActionButtonDanger>
          </ActionButtons>
        </SidebarSection>
      )}

      <SidebarSection title="Details">
        <div className="space-y-4">
          <DetailField
            label="Category"
            value={service.metadata?.category?.replace("_", " ") || "N/A"}
          />
          <DetailField
            label="Availability"
            value={service.metadata?.availability?.replace("_", " ") || "N/A"}
          />
          <DetailField
            label="Posted by"
            value={service.author?.name || service.author?.email || "N/A"}
          />
          <DetailField
            label="Posted on"
            value={new Date(service.createdAt).toLocaleDateString()}
          />
        </div>
      </SidebarSection>
    </>
  );

  return (
    <IndividualViewPageLayout
      breadcrumbItems={breadcrumbItems}
      currentPageUrl={currentPageUrl}
      sidebarContent={sidebarContent}
    >
      {/* Service Header */}
      <div className="mb-6">
        <div className="flex items-start justify-between mb-4">
          <div className="flex-1">
            <div className="flex items-center justify-between gap-4">
              <h2 className="text-2xl font-bold text-gray-900">
                {service.title}
              </h2>
              <SubscribeButton contentId={service.id} />
            </div>
            <div className="flex items-center gap-2 mt-2">
              <span
                className={`px-2 py-1 text-xs font-medium rounded ${getCategoryColor(
                  service.metadata?.category,
                )}`}
              >
                {service.metadata?.category?.replace("_", " ")}
              </span>
              <span
                className={`px-2 py-1 text-xs font-medium rounded ${getPriceTypeColor(
                  service.metadata?.priceType,
                )}`}
              >
                {service.metadata?.priceType?.replace("_", " ")}
              </span>
              <span
                className={`px-2 py-1 text-xs font-medium rounded ${getAvailabilityColor(
                  service.metadata?.availability,
                )}`}
              >
                {service.metadata?.availability?.replace("_", " ")}
              </span>
            </div>
          </div>

          <div className="text-3xl font-bold text-green-600">
            {service.metadata?.priceType === "FREE"
              ? "Free"
              : service.metadata?.price
                ? `$${service.metadata.price}`
                : "Contact for Price"}
          </div>
        </div>

        <p className="text-gray-700 text-lg leading-relaxed">
          {service.description}
        </p>
      </div>

      {/* Service Details */}
      <div className="mb-8">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          Service Details
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Left Column */}
          <div className="space-y-4">
            {service.metadata?.duration && (
              <div className="flex items-start gap-3">
                <Clock className="w-5 h-5 text-gray-400 mt-0.5" />
                <div>
                  <div className="text-sm font-medium text-gray-500">
                    Duration
                  </div>
                  <div className="text-gray-900">
                    {service.metadata.duration}
                  </div>
                </div>
              </div>
            )}

            {service.metadata?.experience && (
              <div className="flex items-start gap-3">
                <Star className="w-5 h-5 text-gray-400 mt-0.5" />
                <div>
                  <div className="text-sm font-medium text-gray-500">
                    Experience
                  </div>
                  <div className="text-gray-900">
                    {service.metadata.experience}
                  </div>
                </div>
              </div>
            )}

            {service.metadata?.qualifications &&
              service.metadata.qualifications.length > 0 && (
                <div className="flex items-start gap-3">
                  <Award className="w-5 h-5 text-gray-400 mt-0.5" />
                  <div>
                    <div className="text-sm font-medium text-gray-500">
                      Qualifications
                    </div>
                    <div className="flex flex-wrap gap-2 mt-1">
                      {service.metadata.qualifications.map(
                        (qual: string, index: number) => (
                          <span
                            key={index}
                            className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full"
                          >
                            <CheckCircle className="w-3 h-3" />
                            {qual}
                          </span>
                        ),
                      )}
                    </div>
                  </div>
                </div>
              )}
          </div>

          {/* Right Column */}
          <div className="space-y-4">
            <div className="flex items-start gap-3">
              <DollarSign className="w-5 h-5 text-gray-400 mt-0.5" />
              <div>
                <div className="text-sm font-medium text-gray-500">Pricing</div>
                <div className="text-gray-900">
                  {service.metadata?.priceType === "FREE"
                    ? "Free Service"
                    : service.metadata?.price
                      ? `$${service.metadata.price} ${service.metadata.priceType
                        ?.toLowerCase()
                        ?.replace("_", " ")}`
                      : "Contact for pricing"}
                </div>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div className="w-5 h-5 text-gray-400 mt-0.5 flex items-center justify-center">
                <div className="w-2 h-2 bg-gray-400 rounded-full"></div>
              </div>
              <div>
                <div className="text-sm font-medium text-gray-500">
                  Service Provider
                </div>
                <div className="text-gray-900">
                  {service.author.name || service.author.email}
                </div>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div className="w-5 h-5 text-gray-400 mt-0.5 flex items-center justify-center">
                <div className="w-2 h-2 bg-gray-400 rounded-full"></div>
              </div>
              <div>
                <div className="text-sm font-medium text-gray-500">Listed</div>
                <div className="text-gray-900">
                  {new Date(service.createdAt).toLocaleDateString("en-US", {
                    weekday: "long",
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Contact Section */}
      {service.metadata?.contactInfo && (
        <div className="mt-12 pt-8 border-t border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            Interested in this service?
          </h3>
          <p className="text-gray-600 mb-4">
            Contact the service provider to discuss your needs and arrange a
            meeting.
          </p>
          <div className="flex items-center gap-2 text-blue-600">
            <Phone className="w-4 h-4" />
            <span className="font-medium">{service.metadata.contactInfo}</span>
          </div>
        </div>
      )}
    </IndividualViewPageLayout>
  );
}
